import React from 'react'
import { Section, GuideHeader, P, UI, Bullets, Steps, Callout } from './GuideParts'
import OpCoRoleHelp from '../../components/opco/admin/OpCoRoleHelp'

export default function OpCoWorkingGuide() {
  return (
    <div className="space-y-8">
      <GuideHeader
        title="Working in an OpCo"
        intro="Your organisation is split into OpCos (operating companies). You work inside one at a time and only see its data."
      />

      <Section number={1} title="What an OpCo Is">
        <P>
          An OpCo is a workspace for one part of the business. Templates, requests, documents, billing and ERP connections belong to an OpCo,
          and what you see on every page is limited to the OpCo you are working in. Your organisation role (Admin, Approver, Reviewer and so on)
          still applies inside it.
        </P>
      </Section>

      <Section number={2} title="Switching Workspace">
        <Steps>
          <li>Look for <UI>Workspace: &lt;name&gt;</UI> in the top bar. It is only shown if you belong to more than one OpCo.</li>
          <li>Open it, search by name if the list is long, and pick another OpCo.</li>
          <li>The page reloads in the new workspace. If you have unsaved edits you are asked first.</li>
        </Steps>
        <P>
          The workspace is part of the page address (<span className="font-mono">/o/&lt;code&gt;/…</span>). If you belong to a single OpCo there is
          no switcher, and its name appears in your user menu.
        </P>
      </Section>

      <Section number={3} title="What You See Inside an OpCo">
        <Bullets>
          <li>Only that OpCo’s templates, requests, documents and emails.</li>
          <li>An <UI>Unassigned</UI> chip on a template or request that belongs to no OpCo yet. Those are hidden inside an OpCo and are handled by Super Admins.</li>
          <li>Settings that apply to the whole organisation (branding, display preferences, custom domain, groups) are the same in every OpCo. Only Admins can change them.</li>
          <li>An Admin belongs to one OpCo. An Admin can make another user of their own OpCo an Admin, but only a Super Admin can put an Admin into a different OpCo.</li>
        </Bullets>
      </Section>

      <Section number={4} title="Admins and Members">
        <P>
          Inside an OpCo you are either its Admin or a member. There is no separate OpCo role: an Admin is simply a user with the
          Admin role on the Users page, and looks after the one OpCo they belong to.
        </P>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <OpCoRoleHelp role="member" />
          <OpCoRoleHelp role="manager" />
        </div>
      </Section>

      <Section number={5} title="Moving a Request to Another OpCo">
        <P>
          Only Super Admins can use <UI>Move to OpCo</UI> on a request. Pick the OpCo, add an optional reason, then choose <UI>Move</UI>.
          A request that was already posted to an ERP or delivered cannot be moved, and its template has to be in the target OpCo first
          (assign an Unassigned template first). The move is recorded in the audit trail.
        </P>
        <Callout kind="info">
          A request with an amber <UI>Unassigned</UI> chip has no OpCo yet. The page says so and shows an <UI>Assign</UI> button to Super Admins.
        </Callout>
      </Section>

      <Section number={6} title="If You See “No OpCo Access”">
        <P>
          “No OpCo access yet” means you have not been added to any OpCo. “You don’t have access to this OpCo” means you opened a workspace
          you are not a member of, use <UI>Go to my workspace</UI>. In both cases ask a Super Admin to add you.
        </P>
      </Section>
    </div>
  )
}
